// ============================================================================
// periodComparisonOrchestrator.js
//
// Simple two-pass snapshot for period comparison.
//
// Flow:
//   1. applyTimeFilter for Period A  (generateArrays runs synchronously)
//   2. Read transitions + variants + sample items  →  snapshotA
//   3. resetFilters
//   4. applyTimeFilter for Period B
//   5. Read transitions + variants + sample items  →  snapshotB
//   6. resetFilters to restore original state
//   7. Return { snapshotA, snapshotB }
//
// generateArrays is synchronous inside applyTimeFilter so the arrays are
// ready to read immediately — no event waiting, no polling, no timeouts.
// ============================================================================

import {
  applyTimeFilterAsync,
  resetFilters,
  taskJSON,
  getFieldUsage,
  getStatusTypeSummary,
  getStatusTypeMappings,
  getEntityUpper,
  getModelMetrics,
} from '@process-advisor/process-miner/processMiner/processMiner';

import { apiProvider } from '@process-advisor/process-miner/api/apiProvider';

// ── Snapshot ──────────────────────────────────────────────────────────────────

const captureSnapshot = (label) => {
  const entityUpper = apiProvider.getEntityUpper ? apiProvider.getEntityUpper() : 'Issue';

  const transitionsJSON = (apiProvider.processStepsTable() || []).slice(0, 40).map((t) => ({
    Transition:            t.step,
    'From Activity':       t.from_activity,
    'To Activity':         t.to_activity,
    'Work items':          t.tasks,
    'Avg duration (hrs)':  t.avg_duration_hrs?.toFixed(2)  ?? 'N/A',
    'Std deviation (hrs)': t.std_deviation_hrs?.toFixed(2) ?? 'N/A',
    [`% total ${entityUpper}s`]:
      t.perc_total_tasks != null ? (t.perc_total_tasks * 100).toFixed(2) : 'N/A',
    'Is rework?': t.loop,
  }));

  const variantsJSON = (apiProvider.variantsTable() || []).slice(0, 30).map((v) => ({
    Variant:               v.variant,
    ID:                    v.variant_id,
    [`${entityUpper}s`]:   v.tasks,
    [`% Total ${entityUpper}s`]:
      v.perc_total_tasks != null ? (Number(v.perc_total_tasks) * 100).toFixed(2) : 'N/A',
    'Is Rework':           v.loop,
    Loops:                 v.loops,
    'Std Deviation (hrs)': v.std_deviation_hrs != null ? Number(v.std_deviation_hrs).toFixed(2) : 'N/A',
    'Avg Duration (hrs)':  v.avg_duration_hrs  != null ? Number(v.avg_duration_hrs).toFixed(2)  : 'N/A',
  }));

  const sampleItems        = taskJSON();
  const fieldUsage         = getFieldUsage();
  const statusTypeSummary  = typeof getStatusTypeSummary  === 'function' ? getStatusTypeSummary()  : null;
  const statusTypeMappings = typeof getStatusTypeMappings === 'function' ? getStatusTypeMappings() : [];

  const metricsHistory = typeof getModelMetrics === 'function'
    ? (getModelMetrics() || []).filter(m => m.work_items > 0).map(m => ({
        [`${entityUpper}s`]:   m.work_items,
        Variants:              m.variants,
        Activities:            m.activities,
        Steps:                 m.steps,
        'Avg Transitions':     m.avg_transitions?.toFixed(1),
        'Avg Duration':        m.cycle_time     != null ? (m.cycle_time     / 3600).toFixed(2) : null,
        'Std Deviation':       m.std_deviation  != null ? (m.std_deviation  / 3600).toFixed(2) : null,
        '% Rework':            m.rework_rate    != null ? (m.rework_rate    * 100 ).toFixed(2) : null,
        avg_flow_efficiency:   m.avg_flow_efficiency ?? null,
      }))
    : [];

  console.log(`[periodComparison] Snapshot "${label}": ${transitionsJSON.length} transitions, ${variantsJSON.length} variants, ${metricsHistory.length} metric rows`);

  return { label, transitionsJSON, variantsJSON, sampleItems, fieldUsage, statusTypeSummary, statusTypeMappings, metricsHistory };
};

// ── Main export ───────────────────────────────────────────────────────────────

export const runPeriodComparison = async ({
  periodA,
  periodB,
  onProgress = () => {},
  originalFilter = null,
}) => {
  if (!periodA?.from || !periodA?.to) throw new Error('periodA.from and periodA.to are required');
  if (!periodB?.from || !periodB?.to) throw new Error('periodB.from and periodB.to are required');

  const labelA = periodA.label || `${periodA.from} – ${periodA.to}`;
  const labelB = periodB.label || `${periodB.from} – ${periodB.to}`;

  // ── Period A ──────────────────────────────────────────────────────────────
  onProgress(`Extracting baseline: ${labelA}…`);
  await applyTimeFilterAsync(periodA.from, periodA.to, getEntityUpper(), false);
  const snapshotA = captureSnapshot(labelA);
  console.log(snapshotA)

  // ── Period B ──────────────────────────────────────────────────────────────
  onProgress(`Extracting comparison: ${labelB}…`);
  await resetFilters(true, 'periodComparisonReset');
  await applyTimeFilterAsync(periodB.from, periodB.to, getEntityUpper(), false);
  const snapshotB = captureSnapshot(labelB);
  console.log(snapshotB)
  // ── Restore ───────────────────────────────────────────────────────────────
  onProgress('Restoring view…');
  await resetFilters(true, 'periodComparisonRestore');
  if (originalFilter?.from && originalFilter?.to) {
    await applyTimeFilterAsync(originalFilter.from, originalFilter.to, getEntityUpper(), false);
  }

  return { snapshotA, snapshotB };
};